import { useMemo } from "react";
import { SxProps, Theme } from "@mui/material/styles";
import FilterableSelect from "../../basic/FilterableSelect";
import { CLIO_ROLE_LABELS, ClioRoleField } from "./clioFieldNames";
import { useClioCustomFieldOptions } from "./useClioCustomFieldOptions";

type ValueMode = "id" | "name";

type ClioCustomFieldSelectProps = {
  field: ClioRoleField;
  value?: number | string;
  onChange: (value: number | string | undefined) => void;
  /** `id` for litigation report filters; `name` for string-based filters. */
  valueMode?: ValueMode;
  label?: string;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  includeAll?: boolean;
  includeNone?: boolean;
  allOptionLabel?: string;
  allOptionValue?: number | string;
  minWidth?: number;
  fullWidth?: boolean;
  required?: boolean;
  /** Extra name options merged when valueMode is `name` (e.g. report-derived values). */
  extraNameOptions?: string[];
};

export default function ClioCustomFieldSelect({
  field,
  value,
  onChange,
  valueMode = "id",
  label,
  size = "small",
  sx,
  includeAll = true,
  includeNone = false,
  allOptionLabel,
  allOptionValue,
  minWidth,
  fullWidth = false,
  required = false,
  extraNameOptions = [],
}: ClioCustomFieldSelectProps) {
  const { options } = useClioCustomFieldOptions(field);

  const selectOptions = useMemo(() => {
    if (valueMode === "name") {
      const names = new Set<string>();
      options.forEach((o) => {
        const name = (o.option ?? "").trim();
        if (name) names.add(name);
      });
      extraNameOptions.forEach((name) => {
        const trimmed = name.trim();
        if (trimmed) names.add(trimmed);
      });
      return [...names]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name }));
    }

    return options.map((option) => ({
      value: option.id,
      label: option.option,
    }));
  }, [extraNameOptions, options, valueMode]);

  return (
    <FilterableSelect
      label={label ?? CLIO_ROLE_LABELS[field]}
      value={value}
      options={selectOptions}
      onChange={onChange}
      size={size}
      sx={sx}
      includeAll={includeAll}
      includeNone={includeNone}
      allOptionLabel={allOptionLabel}
      allOptionValue={allOptionValue}
      minWidth={minWidth}
      fullWidth={fullWidth}
      required={required}
    />
  );
}

export function RaaSelect(props: Omit<ClioCustomFieldSelectProps, "field">) {
  return <ClioCustomFieldSelect field="raa" {...props} />;
}

export function ApSelect(props: Omit<ClioCustomFieldSelectProps, "field">) {
  return <ClioCustomFieldSelect field="ap" {...props} />;
}

export function SmaSelect(props: Omit<ClioCustomFieldSelectProps, "field">) {
  return <ClioCustomFieldSelect field="sma" {...props} />;
}

export function SaSelect(props: Omit<ClioCustomFieldSelectProps, "field">) {
  return <ClioCustomFieldSelect field="sa" {...props} />;
}
